import type { KeyboardEvent, ReactNode } from "react";

import { CellaCanale } from "@/components/panoramica/CellaCanale";
import { CellaNumero } from "@/components/panoramica/CellaNumero";
import { LARGHEZZA_CANALE, colonne } from "@/components/panoramica/colonne";
import { Suggerimento } from "@/components/panoramica/Suggerimento";
import type { CanalePanoramica, TipoCanale } from "@/lib/api";
import { numeriCanale, numeriTotale, statoCanale, type Vista } from "@/lib/panoramica";
import { cn } from "@/lib/utils";

const LARGHEZZA_TABELLA = 1320;

interface TabellaCanaliProps {
  titolo: string;
  sottotitolo?: ReactNode;
  tipo: TipoCanale;
  vista: Vista;
  canali: CanalePanoramica[];
  selezionato: string | null;
  onSeleziona: (marketplaceId: string) => void;
  adesso: Date;
}

const percentuale = (px: number) => `${(px / LARGHEZZA_TABELLA) * 100}%`;

export function TabellaCanali({
  titolo,
  sottotitolo,
  tipo,
  vista,
  canali,
  selezionato,
  onSeleziona,
  adesso,
}: TabellaCanaliProps): JSX.Element {
  const cols = colonne(tipo, vista);
  const totale = numeriTotale(canali);
  const tasto = (e: KeyboardEvent<HTMLTableRowElement>, id: string) => {
    if (e.target !== e.currentTarget) return; // Invio su un link della riga apre il link
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSeleziona(id);
    }
  };
  return (
    <section aria-labelledby={`tabella-${tipo}`}>
      <div className="mb-2 flex items-baseline gap-2.5">
        <h2 id={`tabella-${tipo}`} className="text-md font-semibold text-ink">
          {titolo}
        </h2>
        {sottotitolo}
        <span className="text-label tabular-nums text-ink-3">{canali.length} canali</span>
        {tipo === "buybox" ? (
          <span className="ml-auto text-label text-ink-3">Clic su una riga per confrontarla nel grafico</span>
        ) : null}
      </div>
      <div className="overflow-x-auto rounded-[var(--radius-lg)] border border-line bg-card">
        <table className="w-full min-w-[860px] table-fixed border-collapse">
          <colgroup>
            <col style={{ width: percentuale(LARGHEZZA_CANALE[tipo][vista]) }} />
            {cols.map((c) => (
              <col key={c.id} style={{ width: percentuale(c.larghezza) }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th scope="col" className="border-b border-line-2 bg-paper-2 px-2 pb-2 pl-4 pt-2.5 text-left align-bottom">
                <Intestazione
                  etichetta="Canale"
                  glossario="Canale di vendita, paese, automazione e freschezza: quota di offerte i cui prezzi sono stati controllati nelle ultime 24 h."
                  allinea="sinistra"
                />
              </th>
              {cols.map((c) => (
                <th key={c.id} scope="col" className="border-b border-line-2 bg-paper-2 px-2 pb-2 pt-2.5 text-right align-bottom">
                  <Intestazione etichetta={c.etichetta} glossario={c.glossario} allinea="destra" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {canali.map((canale) => {
              const numeri = numeriCanale(canale);
              const scelto = selezionato === canale.marketplace_id;
              return (
                <tr
                  key={canale.marketplace_id}
                  tabIndex={0}
                  data-selezionato={scelto || undefined}
                  onClick={() => onSeleziona(canale.marketplace_id)}
                  onKeyDown={(e) => tasto(e, canale.marketplace_id)}
                  className={cn(
                    "cursor-pointer transition-colors duration-150 hover:bg-paper-2 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ink",
                    scelto && "bg-key/20 hover:bg-key/25",
                  )}
                >
                  <td className="border-b border-line p-0">
                    <CellaCanale canale={canale} adesso={adesso} />
                  </td>
                  {numeri === null ? (
                    <td colSpan={cols.length} className="border-b border-line px-2 py-2 text-right font-mono text-label text-ink-3">
                      {statoCanale(canale, adesso) === "errore"
                        ? "nessun numero: l'ultimo ricalcolo è fallito"
                        : "in calcolo: i numeri arrivano al prossimo ricalcolo"}
                    </td>
                  ) : (
                    cols.map((c) => (
                      <td key={c.id} className="border-b border-line px-2 py-[7px] text-right align-middle">
                        <CellaNumero
                          {...c.cella(numeri, {
                            canaleId: canale.marketplace_id,
                            etichetta: canale.label,
                            adesso,
                            approvabile: canale.kind !== "amazon",
                          })}
                        />
                      </td>
                    ))
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td className="border-t border-ink/40 bg-paper-2 py-2.5 pl-4 pr-1.5 text-data font-semibold text-ink">Totale</td>
              {cols.map((c) => (
                <td key={c.id} className="border-t border-ink/40 bg-paper-2 px-2 py-2.5 text-right align-middle">
                  <CellaNumero
                    {...c.cella(totale, { canaleId: null, etichetta: "Totale", adesso, approvabile: false })}
                    totale
                  />
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

function Intestazione({
  etichetta,
  glossario,
  allinea,
}: {
  etichetta: string;
  glossario: string;
  allinea: "sinistra" | "destra";
}): JSX.Element {
  return (
    <Suggerimento titolo={etichetta} testo={glossario} allinea={allinea}>
      <span
        tabIndex={0}
        className="cursor-help whitespace-nowrap border-b border-dotted border-ink-4 text-label font-semibold text-ink-2 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
      >
        {etichetta}
      </span>
    </Suggerimento>
  );
}
